// Bid sheet for an unowned team: amount, drop nomination, submit via
// place_bid. Submits and shows whatever the RPC returns verbatim, including
// the budget field on an over-budget error, rather than pre-blocking client
// side beyond basic sanity. Re-submitting on an existing pending bid for the
// same target amends it (place_bid upserts on that unique index).
//
// Budget is a per-bid cap, not a shared pool: a manager can bid up to their
// full budget on more than one team at once, and only pays for the claims
// that win (migration 072). "Pending" below is informational only.
import React, { useEffect, useState } from "react";
import BottomSheet from "../sheets/BottomSheet";
import { useWaiverBids } from "../../hooks/useWaiverBids";
import { useBiddingWindow } from "../../hooks/useBiddingWindow";
import { FIRST_FREE_AGENCY_WEEK, LAST_BID_WEEK, WAIVER_BUDGET_MAX, WAIVER_BUDGET_STEP, WAIVER_MAX_CLAIMS } from "../../utils/season";
import { hapticImpact } from "../../capacitor/haptics";
import ClaimRow, { sortByProcessingOrder, claimsWithoutRoom, ROSTER_SIZE } from "./ClaimRow";

const NO_DROP = "__no_drop__";

export default function BidSheet({ leagueId, memberId, teamSlug, teamName, myRoster, allTeams, onClose }) {
  const emptySlots = Math.max(0, ROSTER_SIZE - myRoster.length);
  const hasOpenSlot = emptySlots > 0;
  // Bidding is open from 10am Sunday to 4am Tuesday Eastern, after weeks 3 to
  // 8 (test leagues at any time). Outside that place_bid refuses, so a working
  // form would only collect a bid it rejects. Weeks 1 to 3 are played with the
  // roster as drafted.
  const biddingWindow = useBiddingWindow();
  const waiversOpen = biddingWindow.state === "open";
  const { placeBid, cancelBid, pending, getBudgets } = useWaiverBids({ leagueId, memberId });
  // NO_DROP is an explicit choice, not the absence of one: with an empty slot
  // the manager can still name a team to drop, and a claim without a drop
  // only lands if a slot is still free when it is processed.
  const [dropSlug, setDropSlug] = useState(hasOpenSlot ? NO_DROP : "");
  const [amount, setAmount] = useState("");
  const [budget, setBudget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const existingBid = pending.find((b) => b.team_id === teamSlug);

  // The roster can arrive after the sheet opens. A full roster has no
  // "don't drop" option, so a default chosen while it was still empty must not
  // survive into a submit that sends no drop team.
  useEffect(() => {
    if (!hasOpenSlot && dropSlug === NO_DROP) setDropSlug("");
  }, [hasOpenSlot, dropSlug]);

  useEffect(() => {
    if (existingBid) {
      setDropSlug(existingBid.drop_team_id || NO_DROP);
      setAmount(String(existingBid.amount));
    }
  }, [existingBid]);

  useEffect(() => {
    let cancelled = false;
    getBudgets().then((res) => {
      if (cancelled || res.error) return;
      const mine = res.budgets.find((b) => b.member_id === memberId);
      if (mine) setBudget(mine.budget);
    });
    return () => { cancelled = true; };
  }, [getBudgets, memberId]);

  // The manager's other claims this window, in the order resolve_waivers will
  // process them: highest amount first, then oldest.
  const otherClaims = sortByProcessingOrder(pending.filter((b) => b.team_id !== teamSlug));

  // Whether every claim without a drop, this one included, still has a slot
  // if they all win. The draft of this bid sits in processing order by the
  // amount typed, and is newest, so it loses ties.
  const draftClaim = {
    id: "__this_bid__",
    team_id: teamSlug,
    amount: Number.isFinite(parseInt(amount, 10)) ? parseInt(amount, 10) : 0,
    drop_team_id: dropSlug === NO_DROP ? null : dropSlug || "__unset__",
    created_at: new Date().toISOString(),
  };
  const allClaims = sortByProcessingOrder([...otherClaims, draftClaim]);
  const withoutRoom = claimsWithoutRoom(allClaims, emptySlots);
  const noDropCount = allClaims.filter((c) => !c.drop_team_id).length;
  const withoutRoomNames = allClaims
    .filter((c) => withoutRoom.has(c.id))
    .map((c) => (c.id === draftClaim.id ? "this bid" : allTeams?.[c.team_id]?.school || c.team_id));

  const amountNum = parseInt(amount, 10);
  const overBudget = budget != null && Number.isFinite(amountNum) && amountNum > budget;
  // The cap is on how many teams a manager has a live claim on, not on
  // submits: amending an existing claim (a resubmit on teamSlug) never adds
  // to the count, so it stays allowed even at the cap.
  const atClaimCap = !existingBid && otherClaims.length >= WAIVER_MAX_CLAIMS;

  const handleSubmit = async () => {
    if (!dropSlug || Number.isNaN(amountNum) || amountNum < 0 || overBudget || atClaimCap) return;
    setSubmitting(true);
    setResult(null);
    const res = await placeBid(teamSlug, dropSlug === NO_DROP ? null : dropSlug, amountNum);
    setSubmitting(false);
    setResult(res);
    if (res?.success) {
      hapticImpact('light');
      setBudget(res.budget);
      setTimeout(onClose, 1200);
    }
  };

  if (!waiversOpen) {
    return (
      <BottomSheet open={!!teamSlug} onClose={onClose} title={teamName}>
        <div style={{ paddingBottom: 8 }}>
          <p style={{ fontSize: 15, color: "var(--color-text-strong)", marginBottom: 8, lineHeight: 1.5 }}>
            {biddingWindow.state === "closed"
              ? "Bidding Is Closed For The Season."
              : biddingWindow.state === "locked"
                ? "Free Agency Is Paused."
                : biddingWindow.state === "resolving"
                  ? "Bidding Has Closed. Bids Resolve At 6am ET."
                  : `Bidding Opens At 10am ET Sunday, After Week ${biddingWindow.state === "waiting" ? biddingWindow.afterWeek : FIRST_FREE_AGENCY_WEEK}.`}
          </p>
          <p style={{ fontSize: 14, color: "var(--color-text-muted)", lineHeight: 1.5 }}>
            {biddingWindow.state === "closed"
              ? `The last window followed week ${LAST_BID_WEEK}, so rosters are fixed for the rest of the season.`
              : `Weeks 1 to ${FIRST_FREE_AGENCY_WEEK} are played with the roster you drafted. After each week from week ${FIRST_FREE_AGENCY_WEEK} to week ${LAST_BID_WEEK}, you can bid for any unowned team from 10am Sunday to 4am Tuesday Eastern, with a budget set by your position in the standings. Bids are sealed, resolve at 6am Tuesday, and winning bids join your roster for the week that has just begun.`}
          </p>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={!!teamSlug} onClose={onClose} title={`Bid on ${teamName}`}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--color-text-muted)", marginBottom: 6 }}>
          Drop from your roster
        </label>
        <select
          value={dropSlug}
          onChange={(e) => setDropSlug(e.target.value)}
          style={{
            width: "100%", height: 44, padding: "0 12px", borderRadius: 12,
            border: "1px solid var(--color-border)", fontSize: 15, color: "var(--color-text-strong)",
          }}
        >
          {hasOpenSlot ? (
            <option value={NO_DROP}>
              Don't drop anyone ({emptySlots} empty {emptySlots === 1 ? "slot" : "slots"})
            </option>
          ) : (
            <option value="">Select a team to drop</option>
          )}
          {myRoster.map((slug) => (
            <option key={slug} value={slug}>{allTeams?.[slug]?.school || slug}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--color-text-muted)", marginBottom: 6 }}>
          Bid amount
        </label>
        <input
          type="number"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0"
          style={{
            width: "100%", height: 44, padding: "0 12px", borderRadius: 12,
            border: "1px solid var(--color-border)", fontSize: 15, color: "var(--color-text-strong)",
          }}
        />
      </div>

      <div style={{ backgroundColor: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
        {budget == null ? (
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>Loading budget...</p>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
              <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>Your budget</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" }}>
                ${budget}
              </span>
            </div>
            <p style={{ fontSize: 13, margin: "4px 0 0", color: overBudget ? "var(--color-danger-text)" : "var(--color-text-muted)" }}>
              {overBudget
                ? `This bid can't exceed your $${budget} budget.`
                : "The most you can bid on this team. You can bid up to this on more than one team at once, but you only pay for the claims that win."}
            </p>
          </>
        )}
      </div>

      {withoutRoomNames.length > 0 && (
        <div style={{ backgroundColor: "var(--color-warning-bg)", border: "1px solid var(--color-warning-fill)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: "var(--color-warning-text)", margin: 0, lineHeight: 1.5 }}>
            You have {noDropCount} claims without a drop and {emptySlots} empty {emptySlots === 1 ? "slot" : "slots"}.
            {" "}If they all win, {withoutRoomNames.join(" and ")} {withoutRoomNames.length === 1 ? "is" : "are"} skipped.
            {" "}{withoutRoom.has("__this_bid__")
              ? "Choose a team to drop above to keep this bid in play."
              : "Update those claims with a team to drop to keep them in play."}
          </p>
        </div>
      )}

      {otherClaims.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
            Your other claims
          </div>
          <p style={{ fontSize: 12, color: "var(--color-text-faint)", margin: "0 0 4px" }}>
            Tried in the order you rank them. Reorder from Waivers on the Lineup tab.
          </p>
          {otherClaims.map((bid, i) => (
            <ClaimRow key={bid.id} bid={bid} teams={allTeams} rank={i + 1} onCancel={cancelBid} noRoomIfAllWin={withoutRoom.has(bid.id)} />
          ))}
        </div>
      )}

      {atClaimCap && (
        <div style={{ backgroundColor: "var(--color-warning-bg)", border: "1px solid var(--color-warning-fill)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: "var(--color-warning-text)", margin: 0, lineHeight: 1.5 }}>
            You have {WAIVER_MAX_CLAIMS} pending claims, the most allowed at once. Cancel one from Waivers on the Lineup tab to make room.
          </p>
        </div>
      )}

      {result?.error && (
        <div style={{ backgroundColor: "var(--color-danger-bg)", border: "1px solid var(--color-danger-border)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 14, color: "var(--color-danger-text)", margin: 0 }}>{result.error}</p>
          {result.budget != null && (
            <p style={{ fontSize: 13, color: "var(--color-danger-text)", margin: "4px 0 0" }}>
              Your budget is ${result.budget}.
            </p>
          )}
        </div>
      )}

      {result?.success && (
        <div style={{ backgroundColor: "var(--color-success-bg)", border: "1px solid var(--color-success-border)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 14, color: "var(--color-success-text)", margin: 0 }}>
            Bid {existingBid ? "updated" : "placed"} for ${amountNum}.
          </p>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={submitting || !dropSlug || amount === "" || overBudget || atClaimCap}
        style={{
          width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
          backgroundColor: "var(--color-brand)", color: "var(--color-bg-card)", fontSize: 16, fontWeight: 700,
          cursor: "pointer", opacity: submitting || !dropSlug || amount === "" || overBudget || atClaimCap ? 0.5 : 1,
        }}
      >
        {submitting ? "Submitting..." : existingBid ? "Update bid" : "Place bid"}
      </button>

      {/* The three rules a manager needs at the moment of bidding. The full
          version is on the rules page. */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--color-bg-subtle)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
          How bidding works
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--color-text-muted)", lineHeight: 1.5 }}>
          <li style={{ marginBottom: 6 }}>
            Bidding closes at 4am ET Tuesday. All bids in the league resolve together at 6am.
          </li>
          <li style={{ marginBottom: 6 }}>
            Your budget is set by your place in the standings: last place gets ${WAIVER_BUDGET_MAX}, and each place higher gets ${WAIVER_BUDGET_STEP} less. It resets every week, so unspent money does not carry over and winning a bid does not reduce next week's budget.
          </li>
          <li style={{ marginBottom: 6 }}>
            Your budget caps each claim, not the total. You can place up to {WAIVER_MAX_CLAIMS} claims at your full budget and only pay for claims that win. Once one wins, any claim below it that costs more than what is left is skipped, so full-budget backups get you at most one team.
          </li>
          <li style={{ marginBottom: 6 }}>
            Claims are tried by rank across the league, every rank 1 claim first, so a rival's higher-ranked claim that wins a team beats your lower-ranked one. Between claims of the same rank, the highest bid wins. If two bids are equal, the manager lower in the standings wins.
          </li>
          <li style={{ marginBottom: 6 }}>
            Your own claims are tried in the order you rank them, not by amount. Reorder them from Waivers on the Lineup tab. You can hold up to {WAIVER_MAX_CLAIMS} at once.
          </li>
          <li>
            Once bids resolve, free agency opens. Any unowned team can be added instantly, first come, first served, until its own game locks 15 minutes before kickoff. A team whose game has started cannot be dropped.
          </li>
        </ul>
      </div>
    </BottomSheet>
  );
}
